import type { ImageProperties } from 'jspdf';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';

const PDF_COMPONENTS_MARGIN = 10;
// TODO implement scaling
const PDF_MOBILE_WIDTH = 0; // 576
const PDF_TABLET_WIDTH = 0; // 768

export const addCanvasToPdf = async(pdf: jsPDF, canvas: HTMLCanvasElement): Promise<void> => {
  const data = canvas.toDataURL('image/png');
  const imgProperties = pdf.getImageProperties(data);

  const pageWidth = pdf.internal.pageSize.getWidth(); // 210 = A4 width
  const pageHeight = pdf.internal.pageSize.getHeight(); // 297 = A4 height

  const imgHeight = imgProperties.height * pageWidth / imgProperties.width;

  const addPagesAndImages = (position: number, imgData: string, myPdf: jsPDF, imgH: number, pageW: number, pageH: number) => {
    pdf.addImage(imgData, 'PNG', 0, position, pageW, imgH, 'report', 'MEDIUM');
    const newPosition = position - pageH;
    const remainingH = imgH + newPosition;

    if (remainingH > 0) {
      pdf.addPage();
      addPagesAndImages(newPosition, imgData, myPdf, imgH, pageW, pageH);
    }
  };

  addPagesAndImages(0, data, pdf, imgHeight, pageWidth, pageHeight);
};

export const downloadPdfFromJSPDF = (pdf: jsPDF, fileName = 'report'): void => {
  pdf.save(`${fileName}.pdf`);
};

export const generatePDF = async(fileName: string, elementsIds: string[]): Promise<void> => {
  const pdf = new jsPDF({ unit: 'px' });
  const pageWidth = pdf.internal.pageSize.getWidth(); // 210 = A4 width
  const pageHeight = pdf.internal.pageSize.getHeight(); // 297 = A4 height
  const usablePageWidth = pageWidth - 2 * PDF_COMPONENTS_MARGIN;

  const getPosition = (position: number, imgH: number): number => (position + imgH > pageHeight - PDF_COMPONENTS_MARGIN)
    ? 0
    : position;

  const getImgPageSize = (imgP: ImageProperties) : { imgWidth: number, imgHeight: number, xPos: number } => {
    if (imgP.width < PDF_MOBILE_WIDTH) {
      return {
        imgWidth: pageWidth * 0.5,
        imgHeight: imgP.height / imgP.width * (pageWidth * 0.5),
        xPos: pageWidth * 0.25,
      };
    }

    if (imgP.width < PDF_TABLET_WIDTH) {
      return {
        imgWidth: pageWidth * 0.75,
        imgHeight: imgP.height / imgP.width * (pageWidth * 0.75),
        xPos: pageWidth * 0.125,
      };
    }
    return {
      imgWidth: usablePageWidth,
      imgHeight: imgP.height / imgP.width * usablePageWidth,
      xPos: PDF_COMPONENTS_MARGIN,
    };
  };

  const addElementToPdf = async(k: number, prevImageFinalPoint: number) => {
    if (k === elementsIds.length) return;

    const isFirstPage = k === 0;
    const element = document.getElementById(elementsIds[k]);
    const positionWithPadding = prevImageFinalPoint + PDF_COMPONENTS_MARGIN;

    if (element !== null) {
      const data = await toPng(element);
      const imgProperties = pdf.getImageProperties(data);
      // canvas width = page width, stretch ratio
      const { imgWidth, imgHeight, xPos } = getImgPageSize(imgProperties);
      let position = getPosition(positionWithPadding, imgHeight);
      const imageFinalPoint = position + imgHeight;

      if (position === 0 && !isFirstPage) {
        pdf.addPage();
        position += PDF_COMPONENTS_MARGIN;
      }

      pdf.addImage(data, 'PNG', PDF_COMPONENTS_MARGIN, position, usablePageWidth, imgHeight, `component-${k}`, 'MEDIUM');

      // Component is bigger than page (tables)
      if (imgHeight > pageHeight - PDF_COMPONENTS_MARGIN ) {
        let remainingH = imgHeight - pageHeight;
        let cPos = 0 - pageHeight;
        while (remainingH > 0) {
          pdf.addPage();
          pdf.addImage(data, 'PNG', xPos, cPos, imgWidth, imgHeight, `component-${k}`, 'MEDIUM');
          remainingH -= pageHeight;
          cPos -= pageHeight;
        }
      }

      if (k === elementsIds.length - 1) {
        return;
      } else {
        await addElementToPdf(k + 1, imageFinalPoint);
      }
    } else {
      await addElementToPdf(k + 1, prevImageFinalPoint);
    }
  };

  await addElementToPdf(0, 0);
  pdf.save(`${fileName}.pdf`);
  pdf.close();
};
